import React, { MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InstantReflex } from 'shiftNotes/_types';

import NoteCardExpandable from './NoteCardExpandable';
import NoteCardReflexStepContent, {
  GUIDANCE_STEP_TYPES,
} from './NoteCardReflexStepContent';

interface NoteCardGuidanceSectionProps {
  instantReflex: InstantReflex;
  noteId: string;
  initiallyExpanded?: boolean;
  onBackgroundClick?: (event: MouseEvent) => void;
}

export default function NoteCardGuidanceSection({
  instantReflex,
  noteId,
  initiallyExpanded = false,
  onBackgroundClick,
}: NoteCardGuidanceSectionProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');

  const steps = useMemo(
    () =>
      [...(instantReflex.steps ?? [])]
        .filter((step) => GUIDANCE_STEP_TYPES.has(step.step_type))
        .sort((a, b) => a.order - b.order),
    [instantReflex.steps]
  );

  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [currentStep, setCurrentStep] = useState(1);

  if (steps.length === 0) return null;

  const totalSteps = steps.length;
  const activeIndex = Math.min(currentStep - 1, totalSteps - 1);
  const activeStep = steps[activeIndex];
  const isLastStep = currentStep >= totalSteps;

  const handleToggle = () => setIsExpanded((v) => !v);
  const handlePrev = () => setCurrentStep((s) => Math.max(1, s - 1));
  const handleNext = () => {
    if (isLastStep) return;
    setCurrentStep((s) => Math.min(totalSteps, s + 1));
  };

  const baseLabel = t('noteCardGuidance.title');
  const stepCounter = `${currentStep}/${totalSteps}`;
  const expandedLabel =
    totalSteps > 1 ? `${baseLabel} ${stepCounter}` : baseLabel;
  const collapsedLabel = totalSteps > 1 ? stepCounter : baseLabel;
  const preview = activeStep.body || activeStep.title || undefined;

  return (
    <NoteCardExpandable
      variant="guidance"
      collapsedLabel={collapsedLabel}
      expandedLabel={expandedLabel}
      currentStep={currentStep}
      totalSteps={totalSteps}
      isExpanded={isExpanded}
      onToggle={handleToggle}
      onPrev={handlePrev}
      onNext={handleNext}
      canGoPrev={currentStep > 1}
      canGoNext={!isLastStep}
      showPagination={totalSteps > 1}
      headerPreview={preview}
      onBackgroundClick={onBackgroundClick}
    >
      <NoteCardReflexStepContent
        step={activeStep}
        instantReflex={instantReflex}
        noteId={noteId}
      />
    </NoteCardExpandable>
  );
}
